import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EMPTY, Observable, expand, forkJoin, map, reduce } from 'rxjs';

import { PayerOrderWire, RentalPayerService } from '@app/rental/rental-payer/rental-payer.service';
import { Partner } from '@app/shared/_models/common';
import { Sales_order } from '@app/shared/_models/sales';
import { DocType, PARTNER_PARTNER_ROLE_BILL, PARTNER_PARTNER_ROLE_DELIV, isOrderLineEditable } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';

// Un dossier compte pour son patient dans tout état sauf annulé ou refusé (documentation.html#front.sales.rental-contract.creation-chapeau-patient).
export const PATIENT_DOSSIER_STATES = 'CREATED,QUOTE,PREPARATION,DELIVERED,INVOICED,PAID,MIGRATION';

export interface PatientDossier {
  id: number;
  state: string;
  customer: number;
  customerName: string;
  patient: number;
  acceptsLines: boolean;
}

// Patient d'un dossier : data.recipient (HAD, tiers payant) sinon le client, même règle que le moteur (loader.rs death_patient_id) ; illisible = null.
export function patientOf(o: { customer?: number | null; data?: any }): number | null {
  const r = o?.data?.recipient;
  if (r === undefined || r === null) return Number(o?.customer) > 0 ? Number(o.customer) : null;
  return Number.isInteger(r) && r > 0 ? r : null;
}

export function partnerName(p?: { firstname?: string | null; name?: string | null } | null): string {
  return `${p?.name ?? ''} ${p?.firstname ?? ''}`.trim();
}

const COUNTED_STATES: ReadonlySet<string> = new Set(PATIENT_DOSSIER_STATES.split(','));

// Dossiers de location de ce patient, lus en résumés (GET /sales/rental_payers/:id, audit 31/10 #37), sans doublon, du plus ancien au plus récent.
// Le client est le tiers de la route (exact), le bénéficiaire celui du résumé ; `names` : noms des clients connus de la page.
export function patientDossiersOfSummaries(pages: { customer: number; orders: PayerOrderWire[] }[], patientId: number, names: Record<number, string> = {}): PatientDossier[] {
  const byId = new Map<number, PatientDossier>();
  for (const { customer, orders } of pages) {
    for (const o of orders ?? []) {
      const id = Number(o?.id) || 0;
      const state = String(o?.state ?? '');
      if (!id || byId.has(id) || !COUNTED_STATES.has(state) || patientOf({ customer, data: { recipient: o.recipient } }) !== patientId) continue;
      byId.set(id, { id, state, customer, customerName: names[customer] ?? '', patient: patientId, acceptsLines: isOrderLineEditable(state, DocType.LOCATION) });
    }
  }
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

// Dossier principal chez ce payeur : le plus ancien qui accepte encore des lignes (JP 29/10/2026) ; payé par un autre, jamais (JP 04/11 : en HAD et hors HAD, deux dossiers).
export function mainDossier(list: PatientDossier[], customer: number): PatientDossier | null {
  return list.find((d) => d.customer === customer && d.acceptsLines) ?? null;
}

export interface NewDossierInput { patient: Partner; payer: Partner | null; station: number | null; user: number | null; store: number; }

// En-tête d'un dossier neuf, sans ligne : mêmes champs que QuotationComponent.createNewOrder + _executeSubmit (client, adresses par défaut, bénéficiaire).
export function newDossierForm(input: NewDossierInput): FormGroup {
  const customer = input.payer ?? input.patient;
  const fg = Sales_order.newOrderFormGroup();
  const bill = Partner.defaultAddress(customer, PARTNER_PARTNER_ROLE_BILL) ?? Partner.addresses(customer)[0];
  const deliv = Partner.defaultAddress(input.patient, PARTNER_PARTNER_ROLE_DELIV) ?? Partner.addresses(input.patient)[0];
  fg.patchValue({
    doc_type: DocType.LOCATION,
    state: SalesOrderState.PREPARATION,
    customer: Number(customer.id),
    station: input.station,
    id_user: input.user,
    store: input.store,
    addr_bill: Number(bill?.id) || 0,
    addr_deliv: Number(deliv?.id) || 0,
    data: input.payer ? { recipient: Number(input.patient.id) } : null,
  });
  return fg;
}

// Mêmes refus que RentalContractComponent.saveBlockedReason, dits avant toute écriture.
export function newDossierBlockedReason(fg: FormGroup, payerDistinct: boolean): string | null {
  if (!(Number(fg.get('customer')?.value) > 0)) return 'Choisissez le patient ou l\'établissement';
  if (!Number(fg.get('addr_deliv')?.value)) {
    return payerDistinct
      ? "Le patient n'a aucune adresse de livraison : ouvrez sa fiche (son nom, en haut) pour en ajouter une, puis recommencez"
      : "Ce client n'a aucune adresse de livraison : ouvrez sa fiche (son nom, en haut) pour en ajouter une, puis recommencez";
  }
  return null;
}

// Pages de 200 résumés (plafond du serveur) : un établissement ou une HAD en a rarement plus de deux.
const SUMMARY_PAGE = 200;

@Injectable({ providedIn: 'root' })
export class RentalPatientService {
  private payers = inject(RentalPayerService);

  // Le patient lui-même, et le payeur quand le patient est son bénéficiaire (HAD) ; résumés sans lignes, toutes les pages (#37 : plus 10 000 dossiers avec leurs lignes).
  dossiersOf$(patientId: number, payerId?: number | null, names: Record<number, string> = {}): Observable<PatientDossier[]> {
    const customers = [...new Set([patientId, Number(payerId) || 0].filter((c) => c > 0))];
    return forkJoin(customers.map((customer) => this.summaries$(customer).pipe(map((orders) => ({ customer, orders })))))
      .pipe(map((pages) => patientDossiersOfSummaries(pages, patientId, names)));
  }

  // Tous les dossiers non annulés du client, repris et clos compris, page après page (before = dernier id reçu).
  private summaries$(customer: number): Observable<PayerOrderWire[]> {
    const page = (before: number | null) => this.payers.getOrders(customer, { start: null, end: null, returned: true, closed: true, canceled: false, q: '', before, limit: SUMMARY_PAGE });
    return page(null).pipe(
      expand((p) => (p?.more && p.orders?.length ? page(p.orders[p.orders.length - 1].id) : EMPTY)),
      reduce((all, p) => all.concat(p?.orders ?? []), [] as PayerOrderWire[]),
    );
  }
}
